import { useNavigate } from "react-router-dom";
import { Bell, ShieldCheck, ArrowLeft, MessageSquare, Ban, HelpCircle } from "lucide-react";

export default function SmsConsent() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen">
      <header className="border-b border-[var(--line)] bg-[#2A4B46] text-[#F9F9F7]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#D36B46] flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" strokeWidth={2.4} />
            </div>
            <span className="font-mono text-sm tracking-[0.18em] uppercase">RemindMe &amp; Do</span>
          </div>
          <button data-testid="consent-back" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#A3A39F] hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12" data-testid="sms-consent-page">
        <p className="label-mono text-[var(--text-2)] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[var(--personal)]" /> Messaging policy</p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">SMS / Text Messaging Consent &amp; Opt-In Policy</h1>
        <p className="text-[var(--text-2)] mt-4 leading-relaxed">
          This page describes how <strong>RemindMe &amp; Do</strong> (operated at
          {" "}<span className="font-mono">Remindmeanddo.minemeanddo.com</span>) collects consent for, and sends,
          SMS text-message reminders. It is provided for our customers and for carrier / messaging-provider review.
        </p>

        <Section icon={MessageSquare} title="Program description">
          RemindMe &amp; Do sends appointment reminders, service-visit notifications, wake-up / check-in reminders,
          bill-due reminders and related account notifications to individuals who have opted in. Messages are
          transactional and scheduled by the account holder who created the reminder.
        </Section>

        <Section icon={ShieldCheck} title="How consent is obtained (opt-in)">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>End users opt in by explicitly selecting <strong>Text (SMS)</strong> as a notification channel for a reminder or service loop inside the app, or by providing their mobile number and agreeing to receive reminders.</li>
            <li>Business customers confirm they have obtained prior express consent from each client before adding that client's mobile number and enabling SMS or voice reminders.</li>
            <li>Consent is never bought, sold, shared or transferred to third parties. Mobile opt-in data is not shared with anyone for marketing purposes.</li>
          </ul>
        </Section>

        <Section icon={MessageSquare} title="Message frequency &amp; cost">
          Message frequency varies based on the reminders each user schedules (for example daily, weekly, or on a
          custom recurrence). <strong>Message and data rates may apply.</strong> Reminders are sent only at the times
          the user configures.
        </Section>

        <Section icon={Ban} title="Opt-out — reply STOP">
          You can cancel SMS messages at any time by replying <strong>STOP</strong> to any message. After you send
          STOP, we will send a one-time confirmation and no further SMS messages will be sent unless you opt back in.
          You may also remove the Text channel from any reminder inside the app.
        </Section>

        <Section icon={HelpCircle} title="Help — reply HELP">
          For help, reply <strong>HELP</strong> to any message, or contact us using the details below. Carrier
          message and data rates may apply.
        </Section>

        <Section icon={ShieldCheck} title="Privacy">
          Phone numbers and opt-in information are used solely to deliver the reminders requested by the user and are
          stored securely. We do not sell or share this information. See our privacy practices or contact us for a copy.
        </Section>

        <div className="mt-10 border border-[var(--line)] bg-white p-6">
          <p className="label-mono text-[var(--text-2)]">Business contact</p>
          <p className="mt-2 text-sm">RemindMe &amp; Do</p>
          <p className="text-sm font-mono">Remindmeanddo.minemeanddo.com</p>
          <p className="text-sm font-mono">Support: support@minemeanddo.com</p>
          <p className="text-sm font-mono">Phone: +1 (888) 576-6585</p>
        </div>

        <p className="text-xs text-[var(--text-2)] mt-6">
          By providing your mobile number and enabling text reminders, you agree to receive recurring automated
          reminder text messages from RemindMe &amp; Do at the number provided. Consent is not a condition of any
          purchase. Reply STOP to cancel, HELP for help.
        </p>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2">
        <Icon className="w-5 h-5 text-[var(--personal)]" /> {title}
      </h2>
      <div className="text-[var(--text-2)] mt-2 leading-relaxed text-sm sm:text-base">{children}</div>
    </section>
  );
}
